import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { sleep } from '@air/call-with-retry';
import {
  setUploadingSpeedAction,
  totalUploadedSizeSelector,
  type UploaderStore,
  uploaderIsUploadingSelector,
  uploadLogger,
} from '@air/redux-uploader';

import KalmanFilter from '../utils/KalmanFilter';

/**
 * How long to sample bytes for each speed measurement.
 * 200ms is short enough to surface a first reading quickly while still
 * capturing meaningful byte deltas for large files.
 */
const DURATION_TO_MEASURE = 700;
const SPEED_CHECK_INTERVAL_PADDING_MS = 200;
const MS_PER_SECOND = 1000;
/**
 * This is how often we want to re-run the speed check.
 * We take whatever the duration to measure is and add a few seconds.
 * */
const INTERVAL_DURATION = DURATION_TO_MEASURE + SPEED_CHECK_INTERVAL_PADDING_MS;
/** This is the weight we want to give to the moving average */
const MOVING_AVERAGE_WEIGHT = 5;

/**
 * This hook watches the currently uploading state and tries to measure how fast the uploading is currently going and updates Redux accordingly
 * with the number of bytes per second it's uploading
 */
export const useCalculateUploadSpeed = () => {
  const store = useStore<UploaderStore>();
  const dispatch = useDispatch();
  const isUploading = useSelector(uploaderIsUploadingSelector);
  const uploadedBytes = useSelector(totalUploadedSizeSelector);
  const currentKalmanStateRef = useRef<KalmanFilter>(
    new KalmanFilter({
      Q: 300, // As per my tests, Q=300 is a good value for a standard deviation of ~20%
      R: 1,
    }),
  );
  const hasUploadedBytes = uploadedBytes > 0;

  const previousSpeedRef = useRef<number>(0);
  const bytesSinceLastCheckRef = useRef<number>(0);
  const timeOfLastCheckRef = useRef<number>(0);

  /**
   * We only want to try and measure the speed if the user is actually uploading files and some bytes have actually been uploaded
   * We don't want to start measuring when we're just fetching download URLs and stuff. We only want to measure when actual bytes are going across the network.
   */
  const shouldCheckSpeed = isUploading && hasUploadedBytes;

  const checkSpeed = useCallback(async () => {
    await sleep(DURATION_TO_MEASURE);

    const isStillUploading = uploaderIsUploadingSelector(store.getState());

    /** If the user isn't uploading anymore, don't continue */
    if (!isStillUploading) {
      uploadLogger.speedCheckStopped();
      return;
    }

    /** get the new number of bytes they've uploaded */
    const updatedBytesUploaded = totalUploadedSizeSelector(store.getState());

    /** figure out the difference between the bytes they've uploaded previously and just now */
    const delta = updatedBytesUploaded - bytesSinceLastCheckRef.current;
    bytesSinceLastCheckRef.current = updatedBytesUploaded;

    /** take difference and divide it by how long we measured */
    const bytesPerSecond = (delta / (performance.now() - timeOfLastCheckRef.current)) * MS_PER_SECOND;
    timeOfLastCheckRef.current = performance.now();

    const speedWithMovingAverage =
      (bytesPerSecond + MOVING_AVERAGE_WEIGHT * (previousSpeedRef.current ?? bytesPerSecond)) /
      (MOVING_AVERAGE_WEIGHT + 1);
    const speed = currentKalmanStateRef.current.filter(speedWithMovingAverage);
    previousSpeedRef.current = speed;

    dispatch(
      setUploadingSpeedAction({
        speed,
      }),
    );
  }, [dispatch, store]);

  useEffect(() => {
    let intervalId: null | number = null;

    if (shouldCheckSpeed) {
      void checkSpeed();

      intervalId = window.setInterval(() => {
        void checkSpeed();
      }, INTERVAL_DURATION);
    }

    return () => {
      /**
       * on unmount, or when shouldCheckSpeed changes,
       * clear the speed check interval
       */
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkSpeed, shouldCheckSpeed]);
};
